import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import "../../styles/runDetailsModal.css";

export default function RunDetailsModal({
  open,
  onClose,
  runData,
  runResult
}) {
  const [showExportSelector, setShowExportSelector] = useState(false);
  const [selectedFieldIndexes, setSelectedFieldIndexes] = useState([]);

  const headers = runResult?.items?.[0] || [];
  const rows = runResult?.items?.slice(1) || [];
  const formatFieldLabel = value =>
    String(value || "")
      .replace(/_/g, " ")
      .toUpperCase();

  useEffect(() => {
    if (!showExportSelector) return;
    setSelectedFieldIndexes(headers.map((_, idx) => idx));
  }, [showExportSelector, headers]);

  useEffect(() => {
    if (open) return;
    setShowExportSelector(false);
    setSelectedFieldIndexes([]);
  }, [open]);

  const columns = headers.map((h, idx) => ({
    name: formatFieldLabel(h),
    selector: row => row[idx],
    sortable: true
  }));

  const selectedHeaders = useMemo(() => {
    return selectedFieldIndexes
      .filter(idx => idx >= 0 && idx < headers.length)
      .map(idx => ({
        idx,
        name: headers[idx],
        label: formatFieldLabel(headers[idx])
      }));
  }, [selectedFieldIndexes, headers]);

  const escapeCSVValue = value => {
    const str = value == null ? "" : String(value);
    if (/[",\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const exportCSV = () => {
    if (!selectedHeaders.length) {
      alert("Please select at least one field to export.");
      return;
    }

    const csvHeaders = selectedHeaders.map(field => escapeCSVValue(field.label));
    const csvRows = rows.map(row =>
      selectedHeaders
        .map(field => escapeCSVValue(row[field.idx]))
        .join(",")
    );
    const csv = [csvHeaders.join(","), ...csvRows].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "run_export.csv";
    a.click();
    URL.revokeObjectURL(url);
    setShowExportSelector(false);
  };

  const toggleField = idx => {
    setSelectedFieldIndexes(prev =>
      prev.includes(idx)
        ? prev.filter(item => item !== idx)
        : [...prev, idx]
    );
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">

        {/* HEADER */}
        <div className="modal-header">
          <h3>Run Details</h3>
          <div className="modal-actions">
            <button
              className="btn secondary"
              onClick={() => setShowExportSelector(true)}
            >
              Export CSV
            </button>
            <button className="btn danger" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {/* PROMPT */}
        {runData?.prompt_text && (
          <div className="modal-prompt">
            <strong>Prompt:</strong> {runData.prompt_text}
          </div>
        )}

        {showExportSelector && (
          <div className="export-selector">
            <div className="export-selector-header">
              <strong>Select fields for CSV export</strong>
              <span>{selectedHeaders.length} selected</span>
            </div>

            <div className="export-selector-actions">
              <button
                className="btn secondary"
                onClick={() => setSelectedFieldIndexes(headers.map((_, idx) => idx))}
              >
                Select All
              </button>
              <button
                className="btn secondary"
                onClick={() => setSelectedFieldIndexes([])}
              >
                Clear All
              </button>
              <button
                className="btn primary"
                onClick={exportCSV}
                disabled={!selectedHeaders.length}
              >
                Download CSV
              </button>
              <button
                className="btn danger"
                onClick={() => setShowExportSelector(false)}
              >
                Cancel
              </button>
            </div>

            <div className="export-selector-grid">
              {headers.map((field, idx) => (
                <label key={`${field}-${idx}`} className="export-selector-item">
                  <input
                    type="checkbox"
                    checked={selectedFieldIndexes.includes(idx)}
                    onChange={() => toggleField(idx)}
                  />
                  <span>{formatFieldLabel(field)}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* TABLE */}
        <DataTable
          columns={columns}
          data={rows}
          pagination
          fixedHeader
          fixedHeaderScrollHeight="60vh"
          highlightOnHover
        />
      </div>
    </div>
  );
}
